import * as React from 'react';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import {
  dashboardApi,
  type DMDashboardData,
} from 'features/DigitalMarketing/api/dashboardApi';

type LegacyDashboardData = DMDashboardData & {
  leads_overview?: {
    total_leads?: number;
    source_distribution?: Record<string, number>;
    avatar?: { avatar_thumbnail?: string | null };
  };
  avatar?: { avatar_thumbnail?: string | null };
};

export const useDashboardData = (enabled: boolean) => {
    const authData = useAuth();
    const [dashboardData, setDashboardData] =
      React.useState<LegacyDashboardData | null>(null);
    const [isLoadingDashboardData, setIsLoadingDashboardData] = React.useState(false);
    const [hasFetchedDashboardData, setHasFetchedDashboardData] = React.useState(false);

    const fetchDashboardData = React.useCallback(async () => {
        if (!authData?.INSTITUTE_ID || !authData?.BRANCH_ID) {
            return;
        }

        setHasFetchedDashboardData(false);
        setIsLoadingDashboardData(true);
        const loadingStartedAt = Date.now();

        try {
            setDashboardData(
              (await dashboardApi.view()) as LegacyDashboardData
            );
        } catch (error) {
            console.error('Unable to fetch digital marketing dashboard:', error);
            setDashboardData(null);
        } finally {
            const remainingDelay = Math.max(450 - (Date.now() - loadingStartedAt), 0);
            window.setTimeout(() => {
                setHasFetchedDashboardData(true);
                setIsLoadingDashboardData(false);
            }, remainingDelay);
        }
    }, [authData?.BRANCH_ID, authData?.INSTITUTE_ID]);

    React.useEffect(() => {
        if (enabled) {
            fetchDashboardData();
        }
    }, [enabled, fetchDashboardData]);

    return {
        dashboardData,
        isLoadingDashboardData,
        hasFetchedDashboardData,
        fetchDashboardData,
    };
};
